import { storyblokEditable } from '@storyblok/react';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { FC, useEffect, useMemo, useRef } from 'react';

import X from '@/assets/close.svg';
import ButtonArrow, {
  ButtonDirection,
} from '@/components/elements/ButtonArrow';
import Container from '@/components/elements/Container';
import Section from '@/components/elements/Section';
import { getStoryblokImageUrl } from '@/lib/utils';

export interface GalleryGridProps {
  blok: any;
  content: any;
}

function Crossfade({ images, index }) {
  return (
    <>
      {images.map((img, i) => (
        <Image
          key={img?.id}
          alt={img?.alt}
          src={getStoryblokImageUrl(img?.filename, 1200, 0)}
          fill
          sizes="100vw"
          className={`object-contain p-4 duration-1000 ${
            index === i ? 'opacity-100' : 'opacity-0'
          }`}
        />
      ))}
    </>
  );
}

function useLockBodyScroll() {
  useEffect(() => {
    const body = document.getElementsByTagName('body')[0];
    const computed = window.getComputedStyle(body).overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = computed;
    };
  }, []);
}

function Overlay({ images, selectedIndex }) {
  const next = useRef<HTMLButtonElement>();
  const router = useRouter();
  useLockBodyScroll();

  function move(dir) {
    router.push(
      {
        // @ts-ignore
        pathname: router.query.slug?.join('/'),
        query: {
          image: (images.length + (selectedIndex + dir)) % images.length,
        },
      },
      null,
      {
        scroll: false,
      }
    );
  }

  function close() {
    router.push(
      {
        // @ts-ignore
        pathname: router.query.slug?.join('/'),
        query: {},
      },
      null,
      {
        scroll: false,
      }
    );
  }

  useEffect(() => {
    function pressKey(e) {
      switch (e.code) {
        case 'Escape':
          close();
          break;
        case 'ArrowLeft':
          move(-1);
          break;
        case 'ArrowRight':
          move(1);
          break;
        default:
          break;
      }
    }
    window.addEventListener('keydown', pressKey);

    return () => {
      window.removeEventListener('keydown', pressKey);
    };
  }, [selectedIndex]);

  useEffect(() => {
    if (next.current) {
      next.current.focus();
    }
  }, [next.current]);

  return (
    <div className="fixed inset-0 z-[999999] bg-theme-black/80 backdrop-blur-lg">
      <button
        className="fixed bottom-3 left-10 z-10 size-[50px] -translate-y-[50%] cursor-pointer rounded-full border-2 border-solid bg-theme-grey/30 p-[5px]"
        onClick={() => move(-1)}
      >
        <ButtonArrow
          className="m-1 fill-theme-white"
          direction={ButtonDirection.LEFT}
        />
      </button>
      <button
        ref={next}
        className="fixed bottom-3 right-10 z-10 size-[50px] -translate-y-[50%] cursor-pointer rounded-full border-2 border-solid bg-theme-grey/30 p-[5px]"
        onClick={() => move(1)}
      >
        <ButtonArrow
          className="m-1 fill-theme-white"
          direction={ButtonDirection.RIGHT}
        />
      </button>
      <button
        className="fixed right-10 top-16 z-10 size-[50px] -translate-y-[50%] cursor-pointer rounded-full border-2 border-solid bg-theme-grey/30 p-[5px] text-theme-white"
        onClick={() => close()}
      >
        <img src={X.src} alt="close" className="m-2" />
      </button>
      <div className="mt-28 align-middle" onClick={close}>
        <Crossfade images={images} index={selectedIndex} />
      </div>
    </div>
  );
}

const GalleryGrid: FC<GalleryGridProps> = ({ blok }) => {
  const router = useRouter();

  function showOverlay(index) {
    router.push(
      {
        // @ts-ignore
        pathname: router.query.slug.join('/'),
        query: { image: index },
      },
      null,
      {
        scroll: false,
      }
    );
  }

  return (
    <Section {...storyblokEditable(blok)} className="imagerow-container">
      <Container size={'widest'} gutter="none">
        <div className="not-prose imagerow flex cursor-pointer flex-col flex-nowrap justify-center gap-2 sm:flex-row md:gap-4">
          {blok.images.map((image, index) => (
            <div
              key={image?.id}
              className="group relative my-2 inline-flex min-w-0 shrink grow-0 md:my-2"
              onClick={() => showOverlay(index)}
            >
              <Image
                alt={image?.alt}
                src={`${image?.filename}/m/0x800`}
                className="!my-0 w-full h-auto"
                width={800}
                height={600}
                sizes="100vw"
              />
            </div>
          ))}
        </div>
      </Container>
    </Section>
  );
};

export function GalleryOverlay({ bloks }) {
  const router = useRouter();

  const images = useMemo(() => {
    return bloks
      .filter((b) => b.component === 'layoutGalleryGrid')
      .reduce((a, b) => {
        a = a.concat(b.images);
        return a;
      }, []);
  }, []);

  // @ts-ignore
  const index = parseInt(router.query.image, 10);

  return (
    <>
      {router.query.image && <Overlay images={images} selectedIndex={index} />}
    </>
  );
}

export default GalleryGrid;
